// エビデンスファイル関連の型定義（docs/contracts/api/api-server.openapi.json の EvidenceFile / ConvertedArtifact / FileProcessingStatus / UploadUrl / DownloadUrl スキーマ準拠）

import { isHttpErrorResponse } from '../../core/utils/http-error.utils';
import { parseOcrFailedReason } from '../../core/utils/ocr-failed-reason.utils';

import type { AttributionUnit } from './required-documents.models';
import type {
  CountedPaginatedResponse,
  UpdatedByUser,
} from '../../core/models/common.models';
import type { OcrFailedReason } from '../../core/utils/ocr-failed-reason.utils';

// AttributionUnit は attribution 関連 API で共通利用するため re-export（Issue #443）
export type { AttributionUnit };
// OcrFailedReason は EvidenceFile の表示側で使うため re-export（Issue #475 Phase 5）
export type { OcrFailedReason };

// ========================================
// 一覧クエリ（Issue #453）
// ========================================
//
// 証憑ファイル一覧 2 endpoint のソート/グルーピング/検索クエリ。
// 仕様の正は docs/contracts/api/api-server.openapi.json（経緯は docs/archive/new-domain/api-proposal/CHANGES-api-server.md の 10 節）。
// viewer は `@domain` を import できないため、API enum と同値の union をローカル定義する
// （値と順序は api-server.openapi.json の enum に一致させること）。

/** 証憑ファイル一覧のソート対象カラム（api-server.openapi.json の sortBy enum と同値） */
export const EVIDENCE_FILE_LIST_SORT_BY_VALUES = [
  'fileName',
  'documentType',
  'attributionTarget',
  'uploadStatus',
  'convertStatus',
  'ocrStatus',
  'uploadedAt',
] as const;

export type EvidenceFileListSortBy =
  (typeof EVIDENCE_FILE_LIST_SORT_BY_VALUES)[number];

/** ソート方向（省略時は asc。nullable なソートキーは asc で末尾 / desc で先頭） */
export const EVIDENCE_FILE_LIST_SORT_ORDER_VALUES = ['asc', 'desc'] as const;

export type EvidenceFileListSortOrder =
  (typeof EVIDENCE_FILE_LIST_SORT_ORDER_VALUES)[number];

/** グルーピング軸（指定時は groupBy 列が第一ソートキーになる。方向は常に昇順固定） */
export const EVIDENCE_FILE_LIST_GROUP_BY_VALUES = [
  'documentType',
  'attributionTarget',
] as const;

export type EvidenceFileListGroupBy =
  (typeof EVIDENCE_FILE_LIST_GROUP_BY_VALUES)[number];

/** 検索文字列の最大長（api-server.openapi.json の search maxLength） */
export const EVIDENCE_FILE_LIST_SEARCH_MAX_LENGTH = 255;

/**
 * 証憑ファイル一覧のサーバサイド query（全て任意）。
 * 全省略時は従来動作（createdAt 昇順・検索なし）。
 */
export type EvidenceFileListQuery = {
  sortBy?: EvidenceFileListSortBy;
  sortOrder?: EvidenceFileListSortOrder;
  groupBy?: EvidenceFileListGroupBy;
  /** ファイル名の部分一致（前後空白除去済み・最大 255 文字） */
  search?: string;
};

// ========================================
// HTTP リクエスト/レスポンス
// ========================================

/** GET /api/v0/tenants/{tenantId}/projects/{projectId}/evidence-files レスポンス */
export type ProjectEvidenceFilesListApiResponse =
  CountedPaginatedResponse<ProjectEvidenceFileItemApiResponse> & {
    projectId: string;
  };

/** ProjectEvidenceFilesListApiResponse の items 要素（FileProcessingStatus に caseId を付加） */
export type ProjectEvidenceFileItemApiResponse =
  FileProcessingStatusApiResponse & {
    caseId: string | null;
  };

/** GET /api/v0/tenants/{tenantId}/projects/{projectId}/cases/{caseId}/evidence-files レスポンス */
export type CaseEvidenceFilesListApiResponse =
  CountedPaginatedResponse<FileProcessingStatusApiResponse> & {
    projectId: string;
    caseId: string;
  };

/** EvidenceFile と紐づく ConvertedArtifact（未変換は null）の組（api-server.openapi.json の FileProcessingStatus） */
export type FileProcessingStatusApiResponse = {
  evidenceFile: EvidenceFileApiResponse;
  convertedArtifact: ConvertedArtifactApiResponse | null;
};

/** 審査対象ファイル（api-server.openapi.json の EvidenceFile） */
export type EvidenceFileApiResponse = {
  publicId: string;
  fileHash: string;
  fileName: string;
  uploadedByUser: UpdatedByUser;
  createdAt: string;
  updatedAt: string;
  // Issue #453: アップロード日時。createdAt とは独立した列で sortBy=uploadedAt のソートキー。
  uploadedAt: string;
  uploadStatus: 'pending' | 'uploaded' | 'error';
  ocrStatus: 'pending' | 'processing' | 'completed' | 'failed';
  /**
   * OCR 失敗理由（Issue #475 Phase 3）。`ocrStatus='failed'` 以外は null。
   *
   * openapi 契約上は必ず載る（nullable）が、Phase 3 以前に作られた固定データ /
   * mock ではキー自体が無いことがあるため optional で受け、parse で null へ正規化する。
   */
  ocrFailedReason?: OcrFailedReason | null;
  // Issue #443: 書類種別紐付け（attribution）。documentTypeCode=null で未分類。
  attribution: EvidenceFileAttributionApiResponse;
};

/**
 * EvidenceFile の attribution（書類種別紐付け）情報（api-server.openapi.json の EvidenceFileAttribution）。
 *
 * `documentTypeCode` が null のとき未分類（DocumentType 未割当）を意味し、
 * `documentTypePublicId` / `attributionUnit` も null になる。
 * 分類済みのとき `attributionUnit` に応じて `caseId` / `employeeId` / `targetMonth` の
 * いずれか 1 つが非 null になる（attributionUnit=project の場合は全て null）。
 */
export type EvidenceFileAttributionApiResponse = {
  documentTypeCode: string | null;
  documentTypePublicId: string | null;
  attributionUnit: AttributionUnit | null;
  caseId: string | null;
  employeeId: string | null;
  targetMonth: string | null;
};

/** EvidenceFile に紐づく変換済みファイル（api-server.openapi.json の ConvertedArtifact、1:0..1） */
export type ConvertedArtifactApiResponse = {
  publicId: string;
  fileHash: string;
  fileName: string;
  createdAt: string;
  status:
    | 'idle'
    | 'awaiting_conversion'
    | 'downloading'
    | 'completed'
    | 'failed';
};

/**
 * POST /api/v0/tenants/{tenantId}/projects/{projectId}/evidence-files/upload-url リクエスト
 * POST /api/v0/tenants/{tenantId}/projects/{projectId}/evidence-files/{evidenceFileId}/replace-url リクエスト
 */
export type UploadUrlApiRequest = {
  fileName: string;
  fileHash: string;
  uploadedBy: string;
  contentType: string;
};

/**
 * POST /api/v0/tenants/{tenantId}/projects/{projectId}/evidence-files/upload-url レスポンス
 * POST /api/v0/tenants/{tenantId}/projects/{projectId}/evidence-files/{evidenceFileId}/replace-url レスポンス
 */
export type UploadUrlApiResponse = {
  publicId: string;
  fileName: string;
  uploadUrl: string;
  expiresIn: number;
};

/** POST /api/v0/tenants/{tenantId}/projects/{projectId}/evidence-files/{evidenceFileId}/pdf/download-url レスポンス */
export type DownloadUrlApiResponse = {
  downloadUrl: string;
  expiresIn: number;
};

/**
 * 証憑ファイルのハッシュ重複を表す api-server の安定判別子（Issue #396）。
 *
 * api-server は全 problem+json 応答に `code`（エラーの type 値）を RFC 7807 の
 * 拡張メンバーとして載せる。detail 文言や enrichment の有無に依存せずに
 * 重複 409 を判別できる唯一の安定キーであるため、型ガードはこの値のみを見る。
 */
export const EVIDENCE_FILE_HASH_CONFLICT_CODE = 'EvidenceFileHashConflict';

/**
 * 409 重複エラー（証憑ファイルアップロード時の重複エラー。Issue #396）
 *
 * upload-url 取得時に (projectId, fileName, fileHash) の三つ組が既存 EvidenceFile と
 * 一致すると api-server が RFC 7807 (application/problem+json) の 409 で返す。
 * 重複 409 は 2 経路あり、どちらも `code` は同じ値になる:
 *   1. 事前チェック経路: `existingFileId` / `existingFileName` を拡張メンバーとして併せて返す
 *   2. DB unique backstop 経路（同時リクエスト時）: 既存レコードを引けないため上記は載らない
 *
 * このため enrichment（`existingFileId` / `existingFileName`）は optional とし、
 * 重複判定そのものには使わない（title / status / detail は viewer では参照しない）。
 */
export type DuplicateEvidenceFileError = {
  status: 409;
  error: {
    code: typeof EVIDENCE_FILE_HASH_CONFLICT_CODE;
    /** 事前チェック経路のみ載る enrichment（重複相手の EvidenceFile 公開ID） */
    existingFileId?: string;
    /** 事前チェック経路のみ載る enrichment（重複相手の EvidenceFile ファイル名） */
    existingFileName?: string;
  };
};

/**
 * PATCH /api/v0/tenants/{tenantId}/projects/{projectId}/evidence-files/{evidenceFileId} リクエスト
 * （api-server.openapi.json の EvidenceFileAttributionUpdateRequest。Issue #443）
 *
 * `documentTypePublicId` を null にすると未分類化（全 attribution をクリア）。
 * 非 null のとき、対象 DocumentType の attributionUnit に応じて以下を指定する:
 *   - attributionUnit=case:     caseId（Case の公開ID）
 *   - attributionUnit=employee: employeeId（社員番号）
 *   - attributionUnit=month:    targetMonth（YYYYMM）
 *   - attributionUnit=project:  追加値なし
 * 排他制約に反する組み合わせは 400 AttributionValidationError を返す。
 */
export type AttributionUpdateApiRequest = {
  documentTypePublicId: string | null;
  caseId?: string | null;
  employeeId?: string | null;
  targetMonth?: string | null;
};

/**
 * 400 InvalidAttributionReassignmentError の情報（Issue #443）。
 *
 * api-server の `mapErrorToHttpResponse` は `InvalidAttributionReassignmentError` を
 * `ProblemDetail` (RFC 9457) に変換し、`detail` フィールドに元エラーの JSON 文字列を
 * 埋め込む。viewer 側は `detail` を JSON.parse したうえで
 * `type === 'InvalidAttributionReassignmentError'` を検査してこの型に narrow する。
 */
export type AttributionValidationError = {
  status: 400;
  type: 'InvalidAttributionReassignmentError';
  field:
    | 'documentTypeId'
    | 'attributionUnit'
    | 'caseId'
    | 'employeeId'
    | 'targetMonth';
  message: string;
};

// ========================================
// フロントエンド内部型
// ========================================

/**
 * プロジェクト単位のエビデンスファイル一覧要素の内部型（Issue #447）。
 *
 * API response 型 (`ProjectEvidenceFileItemApiResponse`) は openapi 契約上
 * トップレベル `caseId` を持つが、viewer 内部では attribution 集約により
 * `evidenceFile.attribution.caseId` のみを参照する。そのため内部型では
 * トップレベル `caseId` を持たず `FileProcessingStatus` と同型として扱う。
 */
export type ProjectEvidenceFileItem = FileProcessingStatus;

export type FileProcessingStatus = {
  evidenceFile: EvidenceFile;
  convertedArtifact: ConvertedArtifact | null;
};

export type EvidenceFile = {
  publicId: string;
  fileHash: string;
  fileName: string;
  uploadedByUser: UpdatedByUser;
  createdAt: Date;
  updatedAt: Date;
  // Issue #453: アップロード日時（sortBy=uploadedAt のソートキー）
  uploadedAt: Date;
  uploadStatus: 'pending' | 'uploaded' | 'error';
  ocrStatus: 'pending' | 'processing' | 'completed' | 'failed';
  /**
   * OCR 失敗理由（Issue #475 Phase 3/5、Issue #600）。
   *
   * 4 状態を持つ:
   *   - `usage_limit_exceeded`: AI 利用上限に到達して OCR を実行できなかった
   *   - `ai_server_error`:      AI サーバー起因のエラーで OCR に失敗した
   *   - `conversion_error`:     PDF 変換に失敗して OCR が始まらなかった（Issue #600）
   *   - `null`:                 理由不明（Phase 3 以前の古いデータ / OCR 失敗していない）
   *
   * 型は `OcrFailedReason`（総括表と共通）で、取りうる値も総括表と同じ。証憑ファイルの
   * 変換失敗は以前アップロードエラー扱いだったが、その経路は到達不能だった
   * （S3 到着時点で OcrProcessing まで進むため）。Issue #600 で総括表（Issue #550）と
   * 同じ OCR 失敗（`conversion_error`）へ合流させた。
   */
  ocrFailedReason: OcrFailedReason | null;
  // Issue #443: 書類種別紐付け（attribution）
  attribution: EvidenceFileAttribution;
};

/**
 * EvidenceFile の attribution 内部型（Issue #443）。
 * API response と同型（値は Date 変換不要のため parse で差異なし）。
 */
export type EvidenceFileAttribution = {
  documentTypeCode: string | null;
  documentTypePublicId: string | null;
  attributionUnit: AttributionUnit | null;
  caseId: string | null;
  employeeId: string | null;
  targetMonth: string | null;
};

export type ConvertedArtifact = {
  publicId: string;
  fileHash: string;
  fileName: string;
  createdAt: Date;
  status:
    | 'idle'
    | 'awaiting_conversion'
    | 'downloading'
    | 'completed'
    | 'failed';
};

export type UploadUrl = {
  publicId: string;
  fileName: string;
  uploadUrl: string;
  expiresIn: number;
};

export type DownloadUrl = {
  downloadUrl: string;
  expiresIn: number;
};

// ========================================
// parse
// ========================================

export const parseEvidenceFileAttribution = (
  a: EvidenceFileAttributionApiResponse,
): EvidenceFileAttribution => ({
  documentTypeCode: a.documentTypeCode,
  documentTypePublicId: a.documentTypePublicId,
  attributionUnit: a.attributionUnit,
  caseId: a.caseId,
  employeeId: a.employeeId,
  targetMonth: a.targetMonth,
});

export const parseEvidenceFile = (
  e: EvidenceFileApiResponse,
): EvidenceFile => ({
  publicId: e.publicId,
  fileHash: e.fileHash,
  fileName: e.fileName,
  uploadedByUser: e.uploadedByUser,
  createdAt: new Date(e.createdAt),
  updatedAt: new Date(e.updatedAt),
  uploadedAt: new Date(e.uploadedAt),
  uploadStatus: e.uploadStatus,
  ocrStatus: e.ocrStatus,
  // 未知の値・キー欠落は null（理由不明）へ正規化する（上限到達に寄せない）
  ocrFailedReason: parseOcrFailedReason(e.ocrFailedReason),
  attribution: parseEvidenceFileAttribution(e.attribution),
});

export const parseConvertedArtifact = (
  a: ConvertedArtifactApiResponse,
): ConvertedArtifact => ({
  publicId: a.publicId,
  fileHash: a.fileHash,
  fileName: a.fileName,
  createdAt: new Date(a.createdAt),
  status: a.status,
});

export const parseFileProcessingStatus = (
  s: FileProcessingStatusApiResponse,
): FileProcessingStatus => ({
  evidenceFile: parseEvidenceFile(s.evidenceFile),
  convertedArtifact: s.convertedArtifact
    ? parseConvertedArtifact(s.convertedArtifact)
    : null,
});

/**
 * ProjectEvidenceFileItem parse 関数（Issue #447）。
 *
 * 内部型は `FileProcessingStatus` と同型のため、`parseFileProcessingStatus` に委譲する。
 * API response のトップレベル `caseId` は viewer 内部で参照されないため無視する
 * （表示側は `evidenceFile.attribution.caseId` を直接参照する）。
 */
export const parseProjectEvidenceFileItem = (
  i: ProjectEvidenceFileItemApiResponse,
): ProjectEvidenceFileItem => parseFileProcessingStatus(i);

export const parseUploadUrl = (r: UploadUrlApiResponse): UploadUrl => ({
  publicId: r.publicId,
  fileName: r.fileName,
  uploadUrl: r.uploadUrl,
  expiresIn: r.expiresIn,
});

export const parseDownloadUrl = (r: DownloadUrlApiResponse): DownloadUrl => ({
  downloadUrl: r.downloadUrl,
  expiresIn: r.expiresIn,
});

/**
 * ファイル処理が終端状態に達しているかどうかを判定する
 * - uploadStatus が 'uploaded' または 'error'
 * - ocrStatus が 'completed' または 'failed'
 * - convertedArtifact が null の場合は終端扱い（後方互換）
 * - convertedArtifact !== null の場合は status が 'completed' または 'failed'
 */
export const isFileProcessingTerminal = (
  file: FileProcessingStatus,
): boolean => {
  const { evidenceFile, convertedArtifact } = file;

  const uploadDone =
    evidenceFile.uploadStatus === 'uploaded' ||
    evidenceFile.uploadStatus === 'error';
  if (!uploadDone) return false;

  const ocrDone =
    evidenceFile.ocrStatus === 'completed' ||
    evidenceFile.ocrStatus === 'failed';
  if (!ocrDone) return false;

  // convertedArtifact が null の場合は終端扱い
  if (convertedArtifact === null) return true;

  return (
    convertedArtifact.status === 'completed' ||
    convertedArtifact.status === 'failed'
  );
};

/**
 * エラーが upload-url の 409 重複エラーかどうかを判定する型ガード（Issue #396）。
 *
 * 判定は `status=409` + 安定判別子 `code` のみで行う。事前チェック経路と
 * DB unique backstop 経路のどちらも同じ `code` を返す一方、enrichment
 * （existingFileId / existingFileName）は事前チェック経路にしか載らないため、
 * enrichment を必須にすると backstop 経路の 409 を重複と判定できなくなる。
 * title / detail は文言変更に弱く、viewer でも使わないため検証しない。
 */
export const isDuplicateEvidenceFileError = (
  error: unknown,
): error is DuplicateEvidenceFileError => {
  if (!isHttpErrorResponse(error)) return false;
  if (error.status !== 409) return false;
  const body: unknown = error.error;
  if (typeof body !== 'object' || body === null) return false;
  const obj = body as Record<string, unknown>;
  return obj['code'] === EVIDENCE_FILE_HASH_CONFLICT_CODE;
};

/** AttributionValidationError の field 値集合 */
const ATTRIBUTION_VALIDATION_FIELDS: ReadonlySet<
  AttributionValidationError['field']
> = new Set([
  'documentTypeId',
  'attributionUnit',
  'caseId',
  'employeeId',
  'targetMonth',
]);

/**
 * エラーが 400 InvalidAttributionReassignmentError かどうかを判定する型ガード（Issue #443）。
 *
 * api-server は RFC 9457 ProblemDetail 形式で 400 を返し、`detail` フィールドに元エラーの
 * JSON 文字列 (`{type, field, message}`) を埋め込む。以下のいずれかの形状を受け付ける:
 *   1. `error.error.detail` が JSON 文字列（api-server 実装）
 *   2. `error.error` 直下に `type: 'InvalidAttributionReassignmentError'` が展開されている
 *      （将来の mock / 非 stringify 実装への互換用）
 */
export const isAttributionValidationError = (
  error: unknown,
): AttributionValidationError | null => {
  if (!isHttpErrorResponse(error)) return null;
  if (error.status !== 400) return null;
  const body: unknown = error.error;
  if (typeof body !== 'object' || body === null) return null;
  const obj = body as Record<string, unknown>;

  // Case 1: ProblemDetail.detail に JSON 文字列が埋め込まれているケース
  const detail = obj['detail'];
  if (typeof detail === 'string') {
    try {
      const parsed: unknown = JSON.parse(detail);
      const narrowed = narrowAttributionValidationPayload(parsed);
      if (narrowed) return narrowed;
    } catch {
      // JSON parse 失敗は通常の Bad Request（別種のバリデーションエラー）として扱う
    }
  }

  // Case 2: 直接展開されているケース（mock 等）
  const narrowed = narrowAttributionValidationPayload(obj);
  if (narrowed) return narrowed;

  return null;
};

const narrowAttributionValidationPayload = (
  payload: unknown,
): AttributionValidationError | null => {
  if (typeof payload !== 'object' || payload === null) return null;
  const obj = payload as Record<string, unknown>;
  if (obj['type'] !== 'InvalidAttributionReassignmentError') return null;
  const field = obj['field'];
  const message = obj['message'];
  if (typeof field !== 'string' || typeof message !== 'string') return null;
  if (
    !ATTRIBUTION_VALIDATION_FIELDS.has(
      field as AttributionValidationError['field'],
    )
  ) {
    return null;
  }
  return {
    status: 400,
    type: 'InvalidAttributionReassignmentError',
    field: field as AttributionValidationError['field'],
    message,
  };
};
